import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { InputWrapper, Text, Link, createUseStyles, useTheme } from '@Sage/hoi-poi-ui';

import styles from './styles/fieldControl';

const useStyles = createUseStyles(styles, 'ForceForm-FieldControl');

function FieldControl({
    Field,
    id,
    type,
    onChange,
    onFocus,
    onBlur,
    onEnter,
    children,
    value,
    placeholder,
    emptyPlaceholder,
    mappedFields,
    error,
    onBulkEdit,
    onBulkCancel,
    isBulk,
    bulkEditLabel,
    bulkCancelLabel,
    rawLabel,
    rawPlaceholder,
    schema,
    activePlaceholder,
    maxSize,
    fileTypes,
    labelForExpand,
    limit,
    ...props
}) {
    const classes = useStyles();
    const [bulkMode, setBulkMode] = useState(isBulk && type !== 'Section');
    const onChangeField = useCallback(
        (input) => {
            const value = input && input.target ? input.target.value : input;
            onChange && onChange(value, id, type, mappedFields);
        },
        [id, onChange, type, mappedFields],
    );
    const theme = useTheme();

    const onFocusField = useCallback(
        (input) => {
            const value = input && input.target ? input.target.value : input;
            onFocus && onFocus(value, id, type);
        },
        [id, onFocus, type],
    );

    const onBlurField = useCallback(
        (input) => {
            const value = input && input.target ? input.target.value : input;
            onBlur && onBlur(value, id, type, mappedFields);
        },
        [id, onBlur, type, mappedFields],
    );

    const onEnterField = useCallback(
        (input) => {
            const value = input && input.target ? input.target.value : input;
            onEnter && onEnter(value, id, type);
        },
        [id, onEnter, type],
    );

    const toggleBulk = useCallback(() => {
        bulkMode ? onBulkEdit?.(id, type) : onBulkCancel?.(id);
        setBulkMode(!bulkMode);
    }, [bulkMode, id, onBulkCancel, onBulkEdit, type]);

    if (!Field) return null;

    const fieldProps = {
        onEnter: onEnterField,
        onChange: onChangeField,
        onFocus: onFocusField,
        onBlur: onBlurField,
        placeholder:
            !value || (Array.isArray(value) && !value.length)
                ? emptyPlaceholder || placeholder
                : placeholder,
        value,
        error: !bulkMode ? error : undefined,
        ...props,
        // List/MultiList resolve options via schema.context.fields[id].table
        schema,
        ...(type === 'File' || type === 'ImagePicker'
            ? { activePlaceholder, maxSize, fileTypes, limit }
            : {}),
        ...(type === 'ExpandableComponent' ? { labelForExpand } : {}),
    };

    if (bulkMode) {
        return (
            <InputWrapper id={id} labelMode="horizontal" {...props}>
                <Text
                    className={classes.bulkText}
                    onClick={toggleBulk}
                    color={theme.colors.grey[200]}
                >
                    {bulkEditLabel}
                </Text>
            </InputWrapper>
        );
    }

    return (
        <>
            <Field id={id} {...fieldProps}>
                {children}
            </Field>
            {isBulk && type !== 'Section' && (
                <Link className={classes.cancelBtn} type="caption" onClick={toggleBulk}>
                    {bulkCancelLabel}
                </Link>
            )}
        </>
    );
}

FieldControl.propTypes = {
    placeholder: PropTypes.string,
    emptyPlaceholder: PropTypes.string,
    value: PropTypes.any,
    error: PropTypes.any,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onEnter: PropTypes.func,
    Field: PropTypes.any,
};

export default React.memo(FieldControl);
